import { NetworkError, toApiError } from '@/api/problem';

/**
 * The platform panel's door to the API — its own, never the shop's `apiFetch`.
 *
 * <p>
 * Two kinds of token pass through here and neither may leak into the other. The <em>platform</em>
 * token signs in the person running the platform; it is held in memory and renewed through
 * `/api/platform/auth/refresh`, whose cookie is path-scoped to the platform's own auth endpoints. A
 * <em>setup</em> token is a short-lived company token for one company that has not gone live,
 * started by the platform token and used against that company's own endpoints (branches, years,
 * numbering, catalogue). Same origin throughout, which is what keeps both refresh cookies
 * `SameSite=Lax`.
 * </p>
 *
 * <p>
 * The shop's client is a module-level singleton configured with the shop's session; sharing it
 * would put a platform token on shop requests or a shop branch header on platform ones. So this
 * module keeps its own state and borrows only the error types, so a refusal reads the same here as
 * at a counter.
 * </p>
 */

let platformToken: string | null = null;
let onSignedOut: (() => void) | null = null;

export function setPlatformToken(token: string | null): void {
  platformToken = token;
}

export function onPlatformSignedOut(handler: (() => void) | null): void {
  onSignedOut = handler;
}

interface RawOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  token?: string | null;
  branchId?: number | null;
}

/** One request, no retry. Throws `ApiError` or `NetworkError` like the shop's client. */
async function raw<T>(path: string, options: RawOptions = {}): Promise<T> {
  const { method = 'GET', body, token = null, branchId = null } = options;
  const headers = new Headers({ Accept: 'application/json' });

  if (body !== undefined) headers.set('Content-Type', 'application/json');
  if (token) headers.set('Authorization', `Bearer ${token}`);
  if (branchId !== null) headers.set('X-Branch-Id', String(branchId));

  let response: Response;

  try {
    response = await fetch(path, {
      method,
      headers,
      credentials: 'include',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (error) {
    throw new NetworkError(error);
  }

  if (!response.ok) throw await toApiError(response);

  if (response.status === 204) return undefined as T;
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

export interface PlatformUser {
  id: number;
  email: string;
  fullName: string;
  role: 'ReadOnly' | 'Support' | 'SuperAdmin';
  mustChangePassword: boolean;
  lastLoginOn: string | null;
}

export interface PlatformAuth {
  accessToken: string;
  accessTokenExpiresOn: string;
  user: PlatformUser;
}

export async function platformSignIn(email: string, password: string): Promise<PlatformAuth> {
  const auth = await raw<PlatformAuth>('/api/platform/auth/login', {
    method: 'POST',
    body: { email, password },
  });
  platformToken = auth.accessToken;
  return auth;
}

/** Exchanges the platform refresh cookie for a new access token; null when there is no session. */
export async function platformRefresh(): Promise<PlatformAuth | null> {
  try {
    const auth = await raw<PlatformAuth>('/api/platform/auth/refresh', { method: 'POST' });
    platformToken = auth.accessToken;
    return auth;
  } catch {
    platformToken = null;
    return null;
  }
}

export async function platformSignOut(): Promise<void> {
  try {
    await raw('/api/platform/auth/logout', { method: 'POST' });
  } finally {
    platformToken = null;
    setupTokens.clear();
  }
}

/**
 * A platform-plane call. A 401 gets one refresh and one retry — the access token is short-lived and
 * expiring mid-setup is ordinary — and only then is the person signed out.
 */
export async function platformFetch<T>(
  path: string,
  options: Omit<RawOptions, 'token'> = {},
): Promise<T> {
  try {
    return await raw<T>(path, { ...options, token: platformToken });
  } catch (error) {
    if (!isUnauthenticated(error)) throw error;
    if (!(await platformRefresh())) {
      onSignedOut?.();
      throw error;
    }
    return raw<T>(path, { ...options, token: platformToken });
  }
}

interface SetupSession {
  accessToken: string;
  expiresOn: string;
}

/**
 * Setup tokens by company. Renewed a minute before they lapse by starting a new session — each one a
 * recorded impersonation row, so a long setup leaves a trail of half-hour sessions rather than one
 * token quietly living past what was recorded.
 */
const setupTokens = new Map<number, SetupSession>();

/**
 * Sessions being started, by company. The company page fires its four reads at once, and each one
 * finding the cache empty started its own session — four rows in the impersonation trail for one
 * sitting, found by driving it. Concurrent callers now wait on the one already under way.
 */
const starting = new Map<number, Promise<SetupSession>>();

async function setupToken(tenantId: number): Promise<string> {
  const held = setupTokens.get(tenantId);
  if (held && new Date(held.expiresOn).getTime() - Date.now() > 60_000) return held.accessToken;

  let pending = starting.get(tenantId);
  if (!pending) {
    pending = platformFetch<SetupSession>(`/api/platform/tenants/${tenantId}/setup-session`, {
      method: 'POST',
    }).finally(() => starting.delete(tenantId));
    starting.set(tenantId, pending);
  }

  const session = await pending;
  setupTokens.set(tenantId, session);
  return session.accessToken;
}

/** Forget a company's setup token — after go-live it would be refused anyway. */
export function dropSetupSession(tenantId: number): void {
  setupTokens.delete(tenantId);
}

/**
 * A call to one company's own endpoints, as its owner, through a setup session. `branchId` is the
 * company-side `X-Branch-Id` for the endpoints that act at a branch.
 */
export async function companyFetch<T>(
  tenantId: number,
  path: string,
  options: Omit<RawOptions, 'token'> = {},
): Promise<T> {
  try {
    return await raw<T>(path, { ...options, token: await setupToken(tenantId) });
  } catch (error) {
    if (!isUnauthenticated(error)) throw error;
    // The session lapsed between the check and the call; one fresh session, then give up.
    setupTokens.delete(tenantId);
    return raw<T>(path, { ...options, token: await setupToken(tenantId) });
  }
}

function isUnauthenticated(error: unknown): boolean {
  return typeof error === 'object' && error !== null && 'isUnauthenticated' in error
    ? Boolean((error as { isUnauthenticated: boolean }).isUnauthenticated)
    : false;
}
